package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.domain.GroupMember
import com.swetdot.swetgram.domain.GroupRoleAssignment
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chat.ChatActionRow
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRow
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetSearchBar
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import com.swetdot.swetgram.ui.profile.ProfileDialog
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.resolveOrNull
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.group_member_joined_on
import swetgram.shared.generated.resources.group_member_you

/**
 * SPS §12.6's Members tab: the member list, the admins, a search, a member card, and removal.
 *
 *
 * PRESENCE AND PRIVACY
 *
 * A member row renders a PERSON, and every person in this app is drawn from the server's profile
 * projection. This screen adds nothing to it and asks nothing extra of it.
 *
 * Concretely: the only presence claim on a row is `SwetAvatar`'s green dot, driven by the same
 * `User.isOnline` the chat list and the search results use. The server projects each member of
 * `listGroupMembers` with `sharesGroup = true` -- which is sound only because `listGroupMembers` is
 * members-only even for a public group -- so somebody whose `show_online` is `shared_groups` is
 * visible here and to nobody outside. A withheld field arrives as null and the mapper turns it into
 * `false`, so the dot is simply not drawn.
 *
 * There is deliberately NO last-seen line, and that is the trap this screen exists on the wrong side
 * of if it is ever added carelessly. `domain.User.lastSeenAt` cannot distinguish "hidden by a
 * privacy setting" from "never recorded", and `User.isOnline` is a non-null Boolean that has already
 * collapsed "withheld" into `false` -- so any sentence built from either would assert something the
 * server did not say. `UserProfile` on the public profile screen keeps that distinction with two
 * nullable fields precisely because it needs to; a member row does not need it, so it makes no
 * claim. Search was the surface that got this wrong once. This one does not repeat it.
 *
 *
 * ROLES AND MODERATION
 *
 * Owner and Admin are labelled on the row, and the member card is where they are CHANGED. Three
 * actions live there now, and each is absent rather than disabled when it is not available: SDS
 * §25.1's disabled state is for something momentarily unavailable, and "you are not the owner" is
 * not a temporary condition.
 *
 * ONE ROLE CONTROL PER CARD, not two. `updateGroupMemberRole` is a single operation whose body
 * carries the target role; a Member's only available change is to Admin and an Admin's only
 * available change is to Member, so a card with both would always be showing one control that does
 * nothing. `Group.nextRoleFor` decides the direction, and the label follows it.
 *
 * BAN IS SEPARATE FROM REMOVE, and both are offered, because they are different decisions with
 * different consequences: a removal can be undone by the person walking back in through a link, and
 * a ban cannot -- only the group's ban list can lift it. Folding them into one control with a
 * checkbox would put the more serious of the two one mis-tap away from the less serious.
 *
 * Every one of the three is CONFIRMED and none is applied optimistically. These are acts on a
 * shared surface: two admins can be looking at the same card, and a row that disappeared before the
 * server agreed would let both of them believe they did it.
 */
@Composable
fun GroupMembersScreen(
    state: GroupMembersUiState,
    /**
     * The signed-in user, so their own row is marked and never offered a removal.
     *
     * A parameter rather than a field of the state, exactly as `ChatListScreen` takes it: the app
     * frame already holds it, and threading it through the view model would give every screen its
     * own copy of a value that belongs to the session.
     */
    me: UserId,
    onBack: () -> Unit,
    onQueryChange: (String) -> Unit,
    /** Opens somebody's public profile, keyed by username as every other caller does. */
    onOpenProfile: (String) -> Unit,
    onRemove: (UserId) -> Unit,
    /**
     * `banGroupMember`. Removes AND bans -- the confirmation says so, because the button cannot.
     *
     * Separate from [onRemove] rather than a flag on it: two different writes, two different
     * consequences, and only one of them can be lifted from the group's ban list.
     */
    onBan: (UserId) -> Unit,
    /**
     * `updateGroupMemberRole`. The role carried is the one the card offered, not a direction.
     *
     * The ViewModel re-derives what the change SHOULD be and drops the call if the two have
     * diverged, so a card left open through somebody else's promotion cannot send a write whose
     * meaning changed underneath it.
     */
    onChangeRole: (UserId, GroupRoleAssignment) -> Unit,
    onTransferOwnership: (UserId) -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** Null disables paging -- for a caller that shows a single page on purpose. */
    onLoadMore: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    val colors = SwetgramThemeTokens.colors
    var openCard by remember { mutableStateOf<GroupMember?>(null) }
    var pendingRemoval by remember { mutableStateOf<GroupMember?>(null) }
    var pendingBan by remember { mutableStateOf<GroupMember?>(null) }
    // The member AND the direction, captured together: the card computed the direction from the
    // state it was drawn with, and re-deriving it inside the dialog would let a background refresh
    // turn a "Make admin" the user agreed to into a demotion between the tap and the confirmation.
    var pendingRoleChange by remember { mutableStateOf<PendingRoleChange?>(null) }
    var pendingTransfer by remember { mutableStateOf<GroupMember?>(null) }
    val visible = state.visible

    Column(modifier.fillMaxSize()) {
        // TODO(localization): the chrome, the empty states and the three confirmations below are
        // still English literals and move into resources (EN + RU). `memberStanding` and
        // `roleLabel` are done -- see the note on `memberStanding` for why the SIGNATURE mattered
        // as much as the strings did.
        SwetTopAppBar(
            title = "Members",
            // The count comes from the GROUP, not from `members.size`: the list is paged, and a
            // header that counted the rows it happened to hold would say "20 members" about a
            // group of 200 and then change as the user scrolled.
            subtitle = state.group?.let { memberCountLine(it.memberCount) }.resolveOrNull(),
            onBack = onBack,
        )

        // SPS §12.6's "search members". SDS §11.3's bar, at the screen margin, and it does NOT
        // take focus on entry -- the list underneath is the point of this screen.
        Column(Modifier.padding(horizontal = Space.screenMargin, vertical = Space.one)) {
            SwetSearchBar(
                value = state.query,
                onValueChange = onQueryChange,
                placeholder = "Search members",
            )
        }

        // Said out loud rather than hidden, because the answer below is genuinely incomplete
        // while it is true: the contract gives `listGroupMembers` no query parameter, so the
        // filter is local and only correct once every page is in hand.
        searchScopeNote(state.searchIncomplete, state.loadingMore)?.let { note ->
            Text(
                text = note,
                style = MaterialTheme.typography.labelMedium,
                color = colors.textSecondary,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = Space.two, vertical = Space.half),
            )
        }

        // Over the list, never in place of it: a failed page must not take away the rows that did
        // load, and on this screen those rows are the only way to reach anybody.
        if (state.error != null) {
            SwetErrorBanner(message = state.error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            // First load. Skeleton rows rather than a spinner (SDS §2.1), so the list lands where
            // the placeholders were instead of replacing a spinner and reflowing.
            state.members.isEmpty() && !state.loadedOnce -> Column(Modifier.fillMaxSize()) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            // A group always has at least one member (the contract's `minimum: 1`, which holds
            // because an owner can never leave), so an empty list here means the search matched
            // nothing -- unless the load failed, in which case the banner above says so.
            visible.isEmpty() && state.query.isNotBlank() -> SwetEmptyState(
                title = "No members found.",
                body = "Try another name or @username.",
            )

            visible.isEmpty() -> SwetEmptyState(
                title = "No members to show.",
                body = "We could not load this group's members.",
                actionText = onRetry?.let { "Try again" },
                onAction = onRetry,
            )

            else -> LazyColumn(Modifier.fillMaxSize().navigationBarsPadding()) {
                items(
                    items = visible,
                    // The user id, not the position: a removal takes a row out of the middle, and
                    // a positional key would animate the wrong one away.
                    key = { it.user.id.value },
                ) { member ->
                    MemberRow(
                        member = member,
                        isMe = member.user.id == me,
                        onClick = { openCard = member },
                    )
                }

                // Only while the list is being browsed. During a search the view model is already
                // sweeping the remaining pages, and a footer would fire a second, competing pull.
                if (state.hasMore && state.query.isBlank()) {
                    item(key = PAGING_FOOTER_KEY) {
                        // Keyed on the item count so it fires once per page rather than on every
                        // recomposition -- and NOT again after a failed page, since the count did
                        // not change. The banner's Retry is the way back.
                        LaunchedEffect(state.members.size) { onLoadMore?.invoke() }
                        SwetListRowSkeleton()
                    }
                }
            }
        }
    }

    openCard?.let { member ->
        MemberCard(
            member = member,
            isMe = member.user.id == me,
            // `domain/Group.kt`'s strict-seniority rule, asked rather than re-derived: an Owner may
            // remove an Admin or a Member, an Admin only a Member, nobody the Owner, and removing
            // yourself is leaving. A copy of that comparison on this screen is how a moderation
            // permission starts meaning two things.
            canRemove = state.mayRemove(member, me),
            canBan = state.mayBan(member, me),
            // Null when there is no role control at all -- either the viewer may not change roles
            // or the target's role is not one this operation can assign.
            roleChange = state.roleChangeFor(member, me),
            canTransferOwnership = state.mayTransferOwnership(member, me),
            zone = zone,
            onViewProfile = {
                openCard = null
                onOpenProfile(member.user.username)
            },
            onRemove = {
                openCard = null
                pendingRemoval = member
            },
            onBan = {
                openCard = null
                pendingBan = member
            },
            onChangeRole = { role ->
                openCard = null
                pendingRoleChange = PendingRoleChange(member, role)
            },
            onTransferOwnership = {
                openCard = null
                pendingTransfer = member
            },
            onDismiss = { openCard = null },
        )
    }

    pendingRemoval?.let { member ->
        ProfileConfirmDialog(
            title = "Remove ${member.user.displayName}?",
            // The consequence they cannot guess, and the one that distinguishes this from a ban:
            // history is bounded at the moment somebody joins (DECISIONS §7's `min_visible_seq`),
            // so coming back is not being restored -- but they CAN come back.
            body = "They lose access to this group's chat. They can join again with an invite " +
                "link, and they will not see anything sent before they rejoin.",
            confirmText = "Remove",
            onConfirm = {
                pendingRemoval = null
                onRemove(member.user.id)
            },
            onDismiss = { pendingRemoval = null },
            busy = state.busy,
        )
    }

    pendingBan?.let { member ->
        ProfileConfirmDialog(
            title = "Ban ${member.user.displayName}?",
            // Three facts, in the order they matter, because none of them is guessable from the
            // word "ban": it removes them as well; a link will not let them back in; and there is
            // exactly one place this can be undone from.
            body = "They are removed from the group and cannot rejoin with an invite link or ask " +
                "to join again. You can lift this later from the group's banned list.",
            confirmText = "Ban",
            onConfirm = {
                pendingBan = null
                onBan(member.user.id)
            },
            onDismiss = { pendingBan = null },
            busy = state.busy,
        )
    }

    pendingRoleChange?.let { pending ->
        val promoting = pending.role == GroupRoleAssignment.ADMIN
        ProfileConfirmDialog(
            title = if (promoting) {
                "Make ${pending.member.user.displayName} an admin?"
            } else {
                "Remove ${pending.member.user.displayName}'s admin rights?"
            },
            // What an admin can actually DO here, taken from SPS §12.7 and from what this build
            // enforces -- not a generic "they will have more permissions". The second sentence of
            // the promotion is the one that matters: an admin can remove people.
            body = if (promoting) {
                "Admins can edit the group, remove members and ban them, and handle join " +
                    "requests. They cannot change roles or delete the group."
            } else {
                "They keep their place in the group and lose every admin control."
            },
            confirmText = if (promoting) "Make admin" else "Remove admin",
            onConfirm = {
                pendingRoleChange = null
                onChangeRole(pending.member.user.id, pending.role)
            },
            onDismiss = { pendingRoleChange = null },
            // Neither direction destroys anything, and both are one tap from being reversed. SDS
            // §11.1 still wants the confirmation -- this is an act on somebody else's standing in a
            // shared space -- but painting it red would say it is a deletion.
            destructive = false,
            busy = state.busy,
        )
    }

    pendingTransfer?.let { member ->
        ProfileConfirmDialog(
            title = "Make ${member.user.displayName} the owner?",
            body = "They will control group settings, roles and deletion. You will become an admin and can then leave the group.",
            confirmText = "Transfer ownership",
            onConfirm = {
                pendingTransfer = null
                onTransferOwnership(member.user.id)
            },
            onDismiss = { pendingTransfer = null },
            destructive = false,
            busy = state.busy,
        )
    }
}

/** A role change the user has been asked to confirm, with the direction the card offered. */
private data class PendingRoleChange(val member: GroupMember, val role: GroupRoleAssignment)

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 6
private const val PAGING_FOOTER_KEY = "group-members-paging-footer"

/**
 * One member.
 *
 * `SwetListRow` verbatim -- SDS §12.1's row, the same one the chat list and the archive use. It was
 * tempting to hand-build a row with a trailing "Remove" button, as `BlockedUsersScreen` does, and
 * that is what the member card below exists to avoid: §12.1's trailing slot is a timestamp and an
 * unread badge, and a second row shape drifts from the first the moment either is touched.
 *
 * The trailing adornment is a WORD, not a coloured pill. SDS §4.8 keeps the UI grayscale with one
 * accent, and §22.2 requires that status never rides on colour alone; "Owner" is already legible to
 * everyone, including a screen reader, without a swatch behind it.
 */
@Composable
private fun MemberRow(
    member: GroupMember,
    isMe: Boolean,
    onClick: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    // "You", "Owner", or "You · Owner". A plain member gets nothing: tagging 498 rows of 500 to
    // distinguish the two that matter is the opposite of what the mark is for.
    //
    // `memberStanding` with no date rather than a second spelling of the same join: the row and the
    // card were building "You · Owner" independently, and two copies of one rule is how the row
    // ends up saying something the card does not.
    val adornment = memberStanding(roleLabel = roleLabel(member.role), isMe = isMe, joinedOn = null)

    SwetListRow(
        title = member.user.displayName,
        preview = "@${member.user.username}",
        onClick = onClick,
        titleTrailing = if (adornment != null || member.user.hasBlackBadge) {
            {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (member.user.hasBlackBadge) SwetBlackBadge()
                    adornment?.let { standing ->
                        if (member.user.hasBlackBadge) Spacer(Modifier.width(Space.half))
                        Text(
                            text = standing.resolve(),
                            style = MaterialTheme.typography.labelSmall,
                            color = colors.textSecondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                }
            }
        } else null,
    ) {
        SwetAvatar(
            displayName = member.user.displayName,
            size = Sizes.avatarMedium,
            // The server's projected presence, and the only presence claim on this row. See the
            // file header for why there is no sentence beside it.
            isOnline = member.user.isOnline,
            surfaceColor = colors.background,
            avatarUrl = member.user.avatarUrl,
        )
    }
}

/**
 * SPS §13.3's compact user card: "Before opening full profile, tapping a user may show a compact
 * user card."
 *
 * It is what a tap opens instead of jumping straight to the profile, and it is what lets the row
 * above stay `SwetListRow` -- the destructive action needs somewhere to live that is not the row's
 * timestamp slot, and §13.3 already describes that somewhere.
 *
 * §13.3 also lists a Message button. It is NOT here, deliberately: opening a direct conversation
 * needs `createOrGetDirect` and its `403 messages_not_allowed` handling, which lives on the public
 * profile this card opens in one tap. A second copy would be a second place for that refusal to be
 * got wrong, and the button would be one row above the one that already works.
 *
 * TODO(design-system): the shell and the action row come from two different feature packages
 * (`ProfileDialog`, `ChatActionRow`), both of which say in their own documentation that they belong
 * in `ui/design`. They are reused rather than copied for exactly the reason those notes give; this
 * is now the third caller waiting on that move.
 */
@Composable
private fun MemberCard(
    member: GroupMember,
    isMe: Boolean,
    canRemove: Boolean,
    canBan: Boolean,
    /** Null means no role control on this card at all. */
    roleChange: GroupRoleAssignment?,
    canTransferOwnership: Boolean,
    zone: TimeZone,
    onViewProfile: () -> Unit,
    onRemove: () -> Unit,
    onBan: () -> Unit,
    onChangeRole: (GroupRoleAssignment) -> Unit,
    onTransferOwnership: () -> Unit,
    onDismiss: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    ProfileDialog(onDismiss = onDismiss) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SwetAvatar(
                displayName = member.user.displayName,
                size = Sizes.avatarLarge,
                isOnline = member.user.isOnline,
                // The dot sits on the dialog's surface, not on the page behind it, or its border
                // reads as a ring instead of a cut-out (SDS §11.5).
                surfaceColor = colors.surface,
                avatarUrl = member.user.avatarUrl,
            )

            Spacer(Modifier.height(Space.two))

            Text(
                text = member.user.displayName,
                style = MaterialTheme.typography.titleLarge,
                color = colors.textPrimary,
                textAlign = TextAlign.Center,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(Modifier.height(Space.half))

            Text(
                text = "@${member.user.username}",
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            // The role in words, and the date they joined -- rendered with the same day formatter
            // the timeline and the blocked list use, so a date never reads one way on one screen
            // and another way on the next. Null when there is nothing true to say.
            memberStanding(
                roleLabel = roleLabel(member.role),
                isMe = isMe,
                // Nullable as a parse fallback: `domain/Group.kt` keeps a member whose date could
                // not be read, because this list is the only place they can be removed from. The
                // line simply omits the date rather than showing 1970.
                joinedOn = member.joinedAt?.let { LocalDay.of(it, zone).render() },
            )?.let { standing ->
                Spacer(Modifier.height(Space.half))
                Text(
                    text = standing.resolve(),
                    style = MaterialTheme.typography.labelMedium,
                    color = colors.textSecondary,
                    textAlign = TextAlign.Center,
                )
            }
        }

        Spacer(Modifier.height(Space.three))

        ChatActionRow(text = "View profile", onClick = onViewProfile)

        // Ordered least to most severe, and the destructive pair sits at the bottom furthest from
        // the tap that opened the card. Every one of them is ABSENT rather than disabled when it is
        // unavailable -- see the file header.
        if (roleChange != null) {
            ChatActionRow(
                text = when (roleChange) {
                    GroupRoleAssignment.ADMIN -> "Make admin"
                    GroupRoleAssignment.MEMBER -> "Remove admin rights"
                },
                onClick = { onChangeRole(roleChange) },
            )
        }


        if (canTransferOwnership) {
            ChatActionRow(text = "Transfer ownership", onClick = onTransferOwnership)
        }

        // Absent on their own row, where leaving is the act and the group screen owns it.
        if (canRemove) {
            ChatActionRow(text = "Remove from group", onClick = onRemove, destructive = true)
        }

        // Last, because it is the only one of the three that the person cannot undo themselves.
        if (canBan) {
            ChatActionRow(text = "Ban from group", onClick = onBan, destructive = true)
        }

        ChatActionRow(text = "Cancel", onClick = onDismiss)
    }
}

/**
 * "Owner · Joined on 03.08.2026", or just the date for a plain member, or null when there is
 * nothing true to say -- a plain member whose join date could not be parsed.
 *
 * [joinedOn] is already formatted by `LocalDay.render()`, the same `dd.MM.yyyy` the chat timeline
 * and the blocked list use, so a date never reads one way on one screen and another way on the next.
 * Null means the date was unreadable, and the line then omits it rather than printing an epoch.
 *
 * Separated out so the join of these three pieces is testable without a dialog around it.
 *
 *
 * WHY THE TYPES CHANGED, WHICH IS THE WHOLE REPAIR AND NOT A TIDY-UP.
 *
 * This took `roleLabel: String?` and returned `String?`, and it kept compiling perfectly after
 * `roleLabel(GroupRole)` became a `UiText`: the call sites simply added `.resolveOrNull()` and
 * handed the RESOLVED word in. So on a Russian phone the card read
 * "You · Владелец · Joined on 12.03.2026" -- three pieces, two languages, one line -- and nothing
 * mechanical objected, because a `String` is a `String` whatever language it is in.
 *
 * [UiText.Joined] exists precisely to make that unsayable, and it only works if the SIGNATURE lets
 * it. Taking a [UiText] means a caller cannot hand over words at all, only a choice of words; and
 * returning a [UiText] means this function cannot mint any of its own. The composition resolves all
 * three parts together, in one language, or it resolves none of them. Leaving the return type a
 * bare `String` would have fixed today's three literals and left the hole they came through open.
 */
internal fun memberStanding(roleLabel: UiText?, isMe: Boolean, joinedOn: String?): UiText? {
    val parts = buildList {
        if (isMe) add(Res.string.group_member_you.text())
        roleLabel?.let { add(it) }
        // The date is a VALUE and not copy -- `LocalDay.render()` already formatted it -- so it
        // travels as a FORMAT ARGUMENT. Concatenating it onto a translated word is the move that
        // put "Joined on 12.03.2026" next to "Владелец" in the first place.
        joinedOn?.let { add(Res.string.group_member_joined_on.text(it)) }
    }
    return if (parts.isEmpty()) null else UiText.Joined(parts, MEMBER_STANDING_SEPARATOR)
}

/** Punctuation, not copy. The same separator `groupSubtitle` uses, for the same reason. */
private const val MEMBER_STANDING_SEPARATOR = " · "
